import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Badge, Card, CardBody, CardHeader } from "reactstrap";
import { toast } from "react-toastify";
import moment from "moment";

import "./index.css";

import api from "service/api";
import newApi from "service/newApi";

import Loading from "components/Loading";

import LibrarySearchType from "enum/library-search-type";
import { getExecutionStatusDescription } from "enum/pipeline-status";

const getLibrarySearchTypeDescription = (search) => {
  switch (search) {
    case LibrarySearchType.EXACT_MATCH:
      return "Pesquisa exata";
    case LibrarySearchType.SIMILARITY:
      return "Similaridade";
    case LibrarySearchType.LOCATION:
      return "Localização";
    case LibrarySearchType.PROXIMITY:
      return "Proximidade";
    default:
      "N/A";
  }
};

const PipelineOverviewSummary = ({
  setPipelineExecuted,
  pipelineExecution,
  setPipelineSummary,
  onBudgetStatusChange,
  executionId,
}) => {
  const { id } = useParams();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  const [dataProcessingConfigured, setDataProcessingConfigured] =
    useState(null);
  const [loadingDataProcessingConfig, setLoadingDataProcessingConfig] =
    useState(false);

  const [budgetStatus, setBudgetStatus] = useState({
    loading: false,
    available: true,
    percentage: 0,
    alertThreshold: null,
    blocked: false,
    error: null,
  });

  const checkBudget = async (clientId) => {
    setBudgetStatus((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const response = await newApi.get(
        `/admin/v1/client/${clientId}/spent-budget`,
      );

      const {
        percentage_used = 0,
        alert_threshold = null,
        is_blocked = false,
      } = response.data;

      const newBudgetStatus = {
        loading: false,
        available: !is_blocked,
        percentage: percentage_used,
        alertThreshold: alert_threshold,
        blocked: is_blocked,
        error: null,
      };

      setBudgetStatus(newBudgetStatus);

      if (onBudgetStatusChange) {
        onBudgetStatusChange(newBudgetStatus);
      }
    } catch (error) {
      console.error("Erro ao verificar budget:", error);
      const errorBudgetStatus = {
        ...budgetStatus,
        loading: false,
        error: "Erro ao verificar orçamento",
      };

      setBudgetStatus(errorBudgetStatus);

      if (onBudgetStatusChange) {
        onBudgetStatusChange(errorBudgetStatus);
      }
    }
  };

  const checkDataProcessingConfig = async (clientId) => {
    if (!clientId) return;

    setLoadingDataProcessingConfig(true);
    try {
      const response = await newApi.get(
        `/v1/data-processing-config/${clientId}`,
      );

      const hasDataFields =
        response.data &&
        response.data.dataFields &&
        response.data.dataFields.length > 0;
      setDataProcessingConfigured(hasDataFields);
    } catch (error) {
      if (error.response?.status === 404) {
        setDataProcessingConfigured(false);
      } else {
        console.error(
          "Erro ao buscar configuração de tratamento de dados:",
          error,
        );
        setDataProcessingConfigured(null);
      }
    } finally {
      setLoadingDataProcessingConfig(false);
    }
  };

  useEffect(() => {
    if (summary?.status === "IDLE") {
      setPipelineExecuted(false);
      return;
    }
    setPipelineExecuted(true);
  }, [summary]);

  useEffect(() => {
    if (summary?.client_id) {
      checkBudget(summary.client_id);
      checkDataProcessingConfig(summary.client_id);
    }
  }, [summary?.client_id]);

  const getDataProcessingStatus = () => {
    if (loadingDataProcessingConfig) {
      return "Verificando...";
    }
    if (dataProcessingConfigured === null) {
      return "Não verificado";
    }
    return dataProcessingConfigured ? "Configurado" : "Não configurado";
  };

  const fetchPipelineSummary = useCallback(async () => {
    setLoading(true);
    api
      .get(
        `/pipeline/${id}/summary${executionId ? `?executionId=${executionId}` : ""}`,
      )
      .then((response) => response.data)
      .then((response) => {
        setSummary(response);
        setPipelineSummary(response);
      })
      .catch((error) => {
        console.error(error);
        toast.error(
          "Houve um problema ao tentar recuperar o sumário da pipeline.",
        );
        return;
      });
    setLoading(false);
  }, [id, executionId]);

  useState(() => {
    fetchPipelineSummary();
  }, [id]);

  const getFormattedDate = useCallback(() => {
    if (!summary?.started_at && !pipelineExecution?.started_at) return "N/A";
    return moment(pipelineExecution?.started_at ?? summary?.started_at).format(
      "DD/MM/YYYY HH:mm",
    );
  }, [pipelineExecution, summary]);

  return (
    <Card className="bg-secondary">
      <CardHeader className="bg-white border-0 custom-card-header">
        Sumário
      </CardHeader>
      <CardBody className="position-relative">
        {loading ? <Loading /> : ""}
        <dl className="grid grid-cols-2 gap-y-2 default-font-size">
          Status da execução -{" "}
          <strong>
            {getExecutionStatusDescription(
              pipelineExecution?.status ?? summary?.status,
            )}
          </strong>{" "}
          {pipelineExecution?.status === "STARTED" ||
          summary?.status !== "IDLE" ? (
            <label>
              / Registrado em - <strong>{getFormattedDate()}</strong>
            </label>
          ) : (
            ""
          )}
          {summary?.execution_description?.trim() ? (
            <>
              <dt className="font-semibold">Descrição do status</dt>
              <dd className="mb-3 item-description">
                {summary?.execution_description ?? "N/A"}
              </dd>
            </>
          ) : (
            ""
          )}
          <hr className="mt-3 mb-3" />
          <dt className="font-semibold">Título</dt>
          <dd className="mb-3 item-description">{summary?.title ?? "N/A"}</dd>
          <dt className="font-semibold">Descrição</dt>
          <dd className="mb-3 item-description">
            {summary?.description ?? "N/A"}
          </dd>
          <dt className="font-semibold">Cliente</dt>
          <dd className="mb-3 item-description">{summary?.client ?? "N/A"}</dd>
          <dt className="font-semibold">Tratamento de dados</dt>
          <dd className="mb-3 item-description">{getDataProcessingStatus()}</dd>
          <dt className="font-semibold">Segmento</dt>
          <dd className="mb-3 item-description">
            {summary?.segment?.name ?? "N/A"}
          </dd>
          <dt className="font-semibold">Biblioteca de vocabulário</dt>
          <dd className="mb-3 item-description">
            {summary?.vocabulary_library?.title ?? "N/A"}
          </dd>
          <dt className="font-semibold">Usar metadados como filtro</dt>
          <dd className="mb-3 item-description">
            {summary?.metadata_is_filter ? "Ativado" : "Desativado"}
          </dd>
          {summary?.event_distribution_column && (
            <>
              <dt className="font-semibold">Distribuição de eventos</dt>
              <dd className="mb-3 item-description">
                {summary.event_distribution_limit} evento(s) por valor de &quot;
                {summary.event_distribution_column}&quot;
              </dd>
            </>
          )}
          {summary?.folders && summary?.folders.length > 0 && (
            <>
              <dt className="font-semibold">Diretórios</dt>
              <div className="w-100 d-flex flex-wrap gap-3 justify-content-start align-items-start">
                {summary?.folders?.map((folder, i) => (
                  <Badge key={i} color="light" className="custom-badge">
                    / {folder}
                  </Badge>
                ))}
              </div>
            </>
          )}
          {summary?.libraries && summary?.libraries.length > 0 && (
            <>
              <dt className="font-semibold mt-3">Bibliotecas</dt>
              <dd>
                <div className="w-100 d-flex flex-wrap gap-3 justify-content-start align-items-start">
                  {summary.libraries.map((library, i) => (
                    <Card
                      key={i}
                      className="border border-gray-300 rounded-lg shadow-sm col-12 col-md-6 col-lg-3"
                      style={{
                        flex: "1 1 auto",
                      }}
                    >
                      <CardHeader className="bg-white border-0 text-center custom-card-header">
                        [ {library?.title} ]
                      </CardHeader>
                      <CardBody className="custom-card-body">
                        <dd className="default-font-size">
                          <dl className="grid grid-cols-2 gap-y-1">
                            <dt className="font-semibold">
                              Mensagem do Consultor
                            </dt>
                            <dd>
                              {library?.consultant_message ? "Sim" : "Não"}
                            </dd>

                            <dt className="font-semibold">
                              Mensagem do Cliente
                            </dt>
                            <dd>{library?.customer_message ? "Sim" : "Não"}</dd>

                            <dt className="font-semibold">Tipo de Busca</dt>
                            <dd>
                              {getLibrarySearchTypeDescription(library?.search)}
                            </dd>
                          </dl>
                        </dd>
                      </CardBody>
                    </Card>
                  ))}
                </div>
              </dd>
            </>
          )}
          {summary?.prompts && summary?.prompts.length > 0 && (
            <>
              <dt className="font-semibold mt-3">Prompts</dt>
              <dd>
                <div className="w-100 d-flex flex-wrap gap-3 justify-content-start align-items-start">
                  {summary.prompts.map((prompt, i) => (
                    <Badge
                      color="success"
                      key={i}
                      style={{ marginTop: "5px", marginRight: "2px" }}
                      onClick={(e) => e.preventDefault()}
                    >
                      {prompt?.title}
                    </Badge>
                  ))}
                </div>
              </dd>
            </>
          )}
        </dl>
      </CardBody>
    </Card>
  );
};

export default PipelineOverviewSummary;
